import React, { useMemo, useState, useEffect, useRef } from "react";
import clsx from "clsx";
import isEmpty from "lodash/isEmpty";
import { useLocation, useNavigate } from "react-router-dom";
import { FiShare2, FiMessageCircle } from "react-icons/fi";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";

import BaseFeedCard from "@/components/common/FeedCard/FeedCard";
import SocialPropertySwiper from "@/pages/LiveVariant/components/Social/SinglePropertyFeed/SocialPropertySwiper";
import { PuzzleFeedItem, type PuzzleFeedCreatedBy } from "./types";
import { cstMoment } from "@common/utils/date.utils";
import moment from "moment-timezone";
import useMixpanelPageViewTracking from "@/hooks/useMixpanelPageViewTracking";
import { useUser } from "@/context/UserContext";
import useLocalStorage from "@/hooks/useLocalStorage";
import { getDisplayPrice } from "@/helpers/helpers";
import { logger } from "@/services/LoggerService";
import { PUZZLE_OPEN_SCOREBOARD_KEY } from "@/constants/puzzle-storage.constants";
import useAgentFollow from "@/hooks/useAgentFollow";
import useOneTapAuth from "@/hooks/useOneTapAuth";
import GuestAuthPromptModal from "@/components/common/GuestAuthPromptModal";
import PropertyLikeButton from "@/components/common/property-like-button/property-like-button";
import { useLikesDrawer } from "@/components/common/likes-drawer/likes-drawer-context";
import usePropertyLike from "@/hooks/usePropertyLike";
import { PROPERTY_LIKE_SIGN_IN_DESCRIPTION } from "@/constants/like.constants";
import {
    MIXPANEL_REGISTER_LOCATION_GOOGLE_ONE_TAP_MODAL,
    MIXPANEL_REGISTER_LOCATION_LIKE_GATE_PROPERTY,
    type MixpanelSignupPromptSource,
} from "@/constants/mixpanel.constants";
import { getPlayablePropertyComments } from "@/services/PropertyService";
import { ScoreBadge } from "@/pages/LiveVariant/components/Puzzle/ScoreboardFeed/guess-grid";
import { convertStatsToGuessCell } from "@/helpers/puzzleGuessCells";
import { ShowMore } from "@re-dev/react-truncate";

function creatorDisplayName(createdBy: PuzzleFeedCreatedBy | null | undefined): string {
    if (!createdBy) return "PriceMe";
    const d = createdBy.displayName?.trim();
    if (d) return d;
    const combined = [createdBy.firstName, createdBy.lastName]
        .filter((p) => p && String(p).trim())
        .join(" ")
        .trim();
    if (combined) return combined;
    const nick = createdBy.nickname?.trim();
    if (nick) return nick;
    return "PriceMe";
}

interface PuzzleFeedCardProps {
    item: PuzzleFeedItem;
    status: "won" | "lost" | "unplayed";
    solvedGuessCount?: number | null;
    onNavigate: () => void;
    onShare: (status: "won" | "lost" | "unplayed") => void;
    isShareLoading: boolean;
}

/**
 * Parses playDate from API (ISO string, "YYYY-MM-DD HH:mm:ss", or date-only "YYYY-MM-DD")
 * into a moment in CST so timestamp and "X hrs ago" are correct.
 */
function parsePlayDateCST(playDate: string): ReturnType<typeof cstMoment> {
    const s = playDate?.trim() || "";
    if (!s) return moment.invalid() as ReturnType<typeof cstMoment>;

    // ISO / UTC: e.g. "2025-02-10T06:00:00.000Z" or with offset
    if (s.includes("T") || s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s)) {
        return moment.utc(playDate).tz("America/Chicago");
    }
    // Date + time in CST: "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD H:mm:ss"
    if (s.includes(" ")) {
        return cstMoment(playDate);
    }
    // Date-only: treat as midnight CST
    return cstMoment(`${playDate} 00:00:00`);
}

function FeedSharerAvatar({ photoUrl, name }: { photoUrl?: string | null; name: string }) {
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        setFailed(false);
    }, [photoUrl]);
    const initial = (name[0] || "P").toUpperCase();
    const showPhoto = Boolean(photoUrl?.trim()) && !failed;
    return (
        <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded-full tw-overflow-hidden tw-bg-[var(--meteorite)] tw-text-white tw-font-semibold tw-text-sm">
            {showPhoto ? (
                <img
                    src={photoUrl!.trim()}
                    alt=""
                    className="tw-w-full tw-h-full tw-object-cover"
                    onError={() => setFailed(true)}
                />
            ) : (
                initial
            )}
        </span>
    );
}

const formatTimestamp = (playDate: string) => {
    const date = parsePlayDateCST(playDate);
    const now = cstMoment();

    if (!date.isValid()) {
        return "";
    }

    const diffHours = Math.max(0, now.diff(date, "hours"));

    // First 24 hours: show hours
    if (diffHours < 24) {
        const hrs = Math.max(Math.floor(diffHours), 1);
        return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
    }

    // After 24 hours: show days
    const diffDays = Math.max(1, Math.floor(now.diff(date, "days")));
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
};

function CommentPreviewAvatar({ photoUrl, name }: { photoUrl?: string | null; name: string }) {
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        setFailed(false);
    }, [photoUrl]);

    const initials =
        name
            ?.trim()
            .split(/\s+/)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase() ?? "")
            .join("") || "PM";
    const showPhoto = Boolean(photoUrl?.trim()) && !failed;

    return (
        <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-overflow-hidden tw-bg-gradient-to-br tw-from-[#8B5CF6] tw-to-[#EC4899] tw-text-white tw-font-semibold tw-text-sm tw-flex-shrink-0">
            {showPhoto ? (
                <img
                    src={photoUrl!.trim()}
                    alt=""
                    aria-hidden="true"
                    className="tw-w-full tw-h-full tw-object-cover"
                    onError={() => setFailed(true)}
                />
            ) : (
                initials
            )}
        </div>
    );
}

const PuzzleFeedCard: React.FC<PuzzleFeedCardProps> = ({
    item,
    status,
    solvedGuessCount,
    onNavigate,
    onShare,
    isShareLoading,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const guestAuthRedirectPath = useMemo(
        () => `${location.pathname}${location.search}`,
        [location.pathname, location.search],
    );
    const { isAuthenticated } = useAuth0();
    const property = item.property;
    const { userState } = useUser();
    const isUserAuthenticated = isAuthenticated || !!userState?.is_created;
    const [visitorStatus] = useLocalStorage<string>("visitor-status", "");
    const [showGuestAuthPrompt, setShowGuestAuthPrompt] = useState(false);
    const [guestAuthDescription, setGuestAuthDescription] = useState(PROPERTY_LIKE_SIGN_IN_DESCRIPTION);
    const [guestAuthSource, setGuestAuthSource] = useState<MixpanelSignupPromptSource>("like");
    const [guestAuthRegisterLocation, setGuestAuthRegisterLocation] = useState<string>(
        MIXPANEL_REGISTER_LOCATION_LIKE_GATE_PROPERTY,
    );
    const propertyRef = useRef<HTMLElement | null>(null);
    const pageName = "Property Feed - Puzzle";
    const { handleGoogle, handleEmail } = useOneTapAuth({
        redirectPath: guestAuthRedirectPath,
        version: "puzzle",
        onClose: () => setShowGuestAuthPrompt(false),
        location: guestAuthRegisterLocation, // Register event
    });

    // Use the custom hook for page view tracking
    useMixpanelPageViewTracking({
        targetRef: propertyRef as React.RefObject<HTMLElement>,
        property: property,
        pageName,
        userState,
        visitorStatus,
        feedType: "puzzle",
        threshold: 1.0,
        debounceDelay: 100,
    });

    const timestampLabel = useMemo(() => formatTimestamp(item.playDate), [item.playDate]);
    const sharerName = useMemo(() => creatorDisplayName(item.createdBy), [item.createdBy]);
    const headerTitle = `${sharerName} shared a puzzle`;
    const bodyText = useMemo(() => String(item.body ?? "").trim(), [item.body]);

    const leadingIcon = (
        <FeedSharerAvatar photoUrl={item.createdBy?.profilePhotoUrl} name={sharerName} />
    );

    const [propertyType, setPropertyType] = useState<string>("Loading...");

    useEffect(() => {
        if (property.propertyType === "Single Family Residential") {
            setPropertyType("House");
        } else if (property.propertyType === "Condominium") {
            setPropertyType("Condo");
        } else if (property.propertyType) {
            setPropertyType(property.propertyType);
        } else {
            setPropertyType("—");
        }
    }, [property.propertyType]);

    const [guessedPropertyResults] = useLocalStorage<any[]>("guessed_property_results", []);

    const listingAgent = property.listingAgent ?? null;
    const listingAgentFirstName = listingAgent?.firstName ?? null;
    const listingAgentLastName = listingAgent?.lastName ?? null;
    const listingAgentNickname = listingAgent?.nickname ?? null;
    const listingAgentAvatar = listingAgent?.img ?? null;
    const followerUserId = userState?.id ? String(userState.id) : null;
    const puzzleSharerUserId = item.createdBy?.id ? String(item.createdBy.id) : null;
    const isPuzzleSharerFollowEligible = Boolean(item.createdBy?.isAgent && puzzleSharerUserId);
    const puzzleSharerNickname = item.createdBy?.nickname ?? null;
    const { isFollowing, isPending: isFollowPending, toggleFollow } = useAgentFollow({
        followerUserId,
        followedUserId: isPuzzleSharerFollowEligible ? puzzleSharerUserId : null,
        initialIsFollowing: item.createdBy?.isFollowing,
        isAuthenticated,
        onRequireAuth: () => {
            setGuestAuthSource("follow"); // source
            setGuestAuthRegisterLocation(MIXPANEL_REGISTER_LOCATION_GOOGLE_ONE_TAP_MODAL); // location
            setGuestAuthDescription("Sign in to follow this agent and get updates"); // description
            setShowGuestAuthPrompt(true);
        },
        mixpanelLocation: "Puzzle Feed",
        nicknameFollowed: puzzleSharerNickname ?? sharerName,
    });
    const { openLikesDrawer } = useLikesDrawer();
    const { isLiked, likeCount, isPending: isLikePending, toggleLike } = usePropertyLike({
        playablePropertyId: item.id,
        initialLikeCount: item.likeCount ?? 0,
        initialLikedByCurrentUser: item.likedByCurrentUser ?? false,
        userId: followerUserId,
        isAuthenticated: isUserAuthenticated,
        onRequireAuth: () => {
            setGuestAuthSource("like"); // source
            setGuestAuthRegisterLocation(MIXPANEL_REGISTER_LOCATION_LIKE_GATE_PROPERTY); // location
            setGuestAuthDescription(PROPERTY_LIKE_SIGN_IN_DESCRIPTION); // description
            setShowGuestAuthPrompt(true);
        },
        mixpanelLocation: "Feed",
        nicknameLiked: puzzleSharerNickname ?? listingAgentNickname ?? null,
        contentType: "puzzle",
    });

    const listingAgentFullName =
        [listingAgentFirstName, listingAgentLastName]
            .filter((part) => !!part && String(part).trim())
            .join(" ") || listingAgentNickname || "";

    const resolvedGuessCount = useMemo(() => {
        if (status !== "won") return null;
        let guessCount: number | null = null;
        if (userState?.id) {
            // For authenticated users, use userGuessCount from item
            guessCount = item.userGuessCount && item.userGuessCount > 0 ? item.userGuessCount : null;
        } else {
            // For guest users, calculate from localStorage cache using guesses array and listPrice
            const cached = guessedPropertyResults?.find(
                (result) => result?.playablePropertyId === item.id,
            );
            if (cached?.guesses) {
                const actualPrice = Number(getDisplayPrice(cached.property ?? property));
                if (actualPrice) {
                    // Find the index where the guess matches the actual price (0-based)
                    const correctGuessIndex = cached.guesses.findIndex((guess: any) => {
                        if (isEmpty(guess) && guess !== 0) return false;
                        const guessValue = typeof guess === "number" ? guess : Number(guess);
                        return guessValue === actualPrice;
                    });
                    // If solved, return the 1-based index (correctGuessIndex + 1)
                    if (correctGuessIndex !== -1) {
                        guessCount = correctGuessIndex + 1;
                    }
                }
            }
        }

        // Fallback to solvedGuessCount prop if available
        guessCount = guessCount ?? solvedGuessCount ?? null;
        if (!guessCount || guessCount === 0) return null;
        return guessCount;
    }, [solvedGuessCount, item.userGuessCount, item.id, status, userState?.id, guessedPropertyResults, property]);

    const hasCompletedPuzzle = status !== "unplayed";

        const { data: commentsData } = useQuery({
        queryKey: ["puzzle-feed-comments-preview", item.id, followerUserId ?? "anonymous"],
        queryFn: () =>
            getPlayablePropertyComments(item.id, {
                include: ["guesses"],
                requestingUserId: followerUserId ?? undefined,
            }),
        staleTime: 60 * 1000,
    });

    const scoreboardCommentCount =
        Number(commentsData?.guess_count ?? 0) + Number(commentsData?.replies_count ?? 0);

    const topCommentPreview = useMemo(() => {
        const firstScoreboardEntry = commentsData?.data?.find((entry) => {
            const hasBody = Boolean(entry?.body?.trim());
            const hasGuesses = Array.isArray(entry?.guesses) && entry.guesses.length > 0;
            return hasBody || hasGuesses;
        });
        if (!firstScoreboardEntry) return null;
        const createdAtLabel = firstScoreboardEntry.createdAt ? moment(firstScoreboardEntry.createdAt).fromNow() : "";
        const guesses = convertStatsToGuessCell(
            firstScoreboardEntry.guesses || [],
            Number(getDisplayPrice(property)),
        );
        return {
            username: firstScoreboardEntry.username,
            body: String(firstScoreboardEntry.body ?? "").trim(),
            profilePhoto: firstScoreboardEntry.profilePhoto ?? null,
            createdAtLabel,
            guesses,
            hasGuesses: guesses.length > 0,
            showScoreLeaderboard: firstScoreboardEntry.showScoreLeaderboard ?? true,
        };
    }, [commentsData, property]);

    const shareAriaLabel = useMemo(() => {
        if (isShareLoading) {
            return "Sharing your score";
        }
        return hasCompletedPuzzle ? "Share your score" : "Share this puzzle";
    }, [hasCompletedPuzzle, isShareLoading]);

    const handleShareClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (isShareLoading || !hasCompletedPuzzle) return;
        onShare(status);
    };

    const navigateToScoreboard = () => {
        try {
            sessionStorage.setItem(PUZZLE_OPEN_SCOREBOARD_KEY, "true");
        } catch (err) {
            logger.warn("Failed to set puzzle-open-scoreboard flag in sessionStorage", {
                context: "PuzzleFeedCard.navigateToScoreboard",
                error: err instanceof Error ? err.message : String(err),
            });
        }
        onNavigate();
    };

    const handleScoreboardClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        navigateToScoreboard();
    };

    const handleFollowClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        toggleFollow();
    };

    const hasBody = bodyText.length > 0;

    const propertyFacts = [
        { label: "Beds", value: property.bedrooms ?? "—" },
        { label: "Baths", value: property.bathrooms ?? "—" },
        {
            label: "Sq. Ft",
            value: property.squareFootage === 0 || !property.squareFootage ? "Unknown" : property.squareFootage,
        },
        { label: "Type", value: propertyType },
    ];

    const media = (
        <>
            <div
                className="tw-relative tw-cursor-pointer"
                onClick={onNavigate}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onNavigate();
                    }
                }}
            >
                <SocialPropertySwiper property={property} disableFancybox={true} />
                <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-10 tw-bg-gradient-to-b tw-from-black/10 tw-via-black/50 tw-to-black/90 tw-w-full !tw-rounded-b-lg tw-pb-3">
                    <div className="tw-px-4 tw-pt-5 tw-pb-2">
                        <div className="tw-text-white tw-font-merriweather tw-text-[18px] tw-leading-snug">
                            {property.streetAddress} | {property.city}
                        </div>
                    </div>
                    <div className="tw-flex tw-flex-row tw-justify-between tw-gap-3 tw-px-4 tw-pb-2 tw-opacity-95">
                        {propertyFacts.map(({ label, value }) => (
                            <div key={label} className="tw-flex-1 tw-text-center tw-text-white">
                                <div className="tw-text-[13px] tw-tracking-[0.08em] tw-text-gray-200">{label}</div>
                                <div className="tw-text-2xl tw-font-semibold tw-leading-snug">{value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );

    const solvedBadgeLabel =
        resolvedGuessCount != null
            ? `Solved · ${resolvedGuessCount} ${resolvedGuessCount === 1 ? "guess" : "guesses"}`
            : "Solved";

    const badgeOverlay =
        status === "won" ? (
            <div
                className="tw-absolute tw-top-3 tw-right-3 tw-z-[20] tw-pointer-events-none"
                aria-live="polite"
                aria-label={solvedBadgeLabel}
            >
                <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-black/60 tw-px-2.5 tw-py-1 tw-text-white tw-text-[13px] tw-font-semibold tw-leading-none">
                    <IoCheckmarkCircleOutline
                        className="tw-text-[#4ADE80] tw-flex-shrink-0"
                        size={18}
                        aria-hidden="true"
                    />
                    <span>{solvedBadgeLabel}</span>
                </div>
            </div>
        ) : null;

    const actions = (
        <>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5 tw-py-0.5">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <PropertyLikeButton
                        isLiked={isLiked}
                        likeCount={likeCount}
                        isDisabled={isLikePending}
                        onClick={(event) => {
                            event.stopPropagation();
                            toggleLike();
                        }}
                        onCountClick={(event) => {
                            event.stopPropagation();
                            openLikesDrawer({
                                contentType: "puzzle",
                                contentId: item.id,
                                likeCount,
                                location: "Feed",
                            });
                        }}
                    />
                    <button
                        type="button"
                        aria-label="Open scoreboard comments"
                        className="tw-inline-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-0 tw-text-gray-700"
                        onClick={handleScoreboardClick}
                    >
                        <FiMessageCircle className="tw-text-[24px]" />
                    </button>
                    {scoreboardCommentCount > 0 && (
                        <span className="tw-text-[14px] tw-ml-[-15px] tw-leading-none tw-text-[#4B5563] tw-font-medium">
                            {scoreboardCommentCount}
                        </span>
                    )}
                    <button
                        type="button"
                        aria-label={shareAriaLabel}
                        className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-300 tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        onClick={handleShareClick}
                        disabled={isShareLoading || !hasCompletedPuzzle}
                    >
                        <FiShare2 className="tw-text-[24px]" />
                    </button>
                </div>

                {(listingAgentFullName || listingAgentNickname || listingAgentAvatar) && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!listingAgentNickname) return;
                            navigate(`/profile/${encodeURIComponent(listingAgentNickname)}`);
                        }}
                        disabled={!listingAgentNickname}
                        aria-disabled={!listingAgentNickname}
                        className={clsx(
                            "tw-flex tw-items-center tw-gap-2 tw-pr-2 tw-py-0 tw-bg-transparent tw-border-0 tw-no-underline tw-transition-colors",
                            listingAgentNickname
                                ? "tw-cursor-pointer hover:tw-opacity-90"
                                : "tw-cursor-default tw-opacity-60 tw-pointer-events-none",
                        )}
                    >
                        <div className="tw-relative tw-w-10 tw-h-10 tw-rounded-full tw-overflow-hidden tw-flex-shrink-0">
                            {listingAgentAvatar ? (
                                <img
                                    src={listingAgentAvatar}
                                    alt={listingAgentFullName || listingAgentNickname || "Listing Agent"}
                                    className="tw-w-full tw-h-full tw-object-cover tw-rounded-full"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = "none";
                                        const nextSibling = target.nextSibling as HTMLElement;
                                        if (nextSibling) {
                                            nextSibling.style.display = "flex";
                                        }
                                    }}
                                />
                            ) : null}
                            <div
                                className={clsx(
                                    "tw-w-full tw-h-full tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-gray-500 tw-text-sm",
                                    { "tw-hidden": listingAgentAvatar },
                                )}
                            >
                                <span>
                                    {(listingAgentFirstName || listingAgentLastName || listingAgentNickname)
                                        ?.toString()
                                        .slice(0, 2)
                                        .toUpperCase() || "LA"}
                                </span>
                            </div>
                        </div>
                        <div className="tw-flex tw-flex-col tw-items-start tw-text-left">
                            <span className="tw-text-sm tw-font-semibold tw-text-[#111827]">
                                {listingAgentFullName || listingAgentNickname}
                            </span>
                            <span className="tw-text-[12px] tw-text-[#6B7280]">Listing Agent</span>
                        </div>
                    </button>
                )}
            </div>

            {scoreboardCommentCount > 0 && topCommentPreview && (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={handleScoreboardClick}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            event.stopPropagation();
                            navigateToScoreboard();
                        }
                    }}
                    className="tw-w-full tw-text-left tw-bg-transparent tw-border-0 tw-p-0 hover:tw-opacity-95"
                >
                    <div className="tw-border-t tw-border-gray-200 tw-py-3">
                        <div className="tw-flex tw-items-start tw-gap-3">
                            <CommentPreviewAvatar
                                photoUrl={topCommentPreview.profilePhoto}
                                name={topCommentPreview.username || "PriceMe"}
                            />
                            <div className="tw-flex tw-flex-col tw-items-start tw-text-left">
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <span
                                        className="tw-text-black tw-text-sm tw-font-semibold"
                                        style={{ fontFamily: "Inter, sans-serif" }}
                                    >
                                        {topCommentPreview.username}
                                    </span>
                                    {topCommentPreview.createdAtLabel && (
                                        <span
                                            className="tw-text-xs tw-font-normal tw-text-[#6A7282]"
                                            style={{ fontFamily: "Inter, sans-serif" }}
                                        >
                                            {topCommentPreview.createdAtLabel}
                                        </span>
                                    )}
                                </div>
                                {topCommentPreview.hasGuesses && (
                                    <>
                                        {topCommentPreview.showScoreLeaderboard ? (
                                            hasCompletedPuzzle && isUserAuthenticated ? (
                                                <div className="tw-mt-1">
                                                    <ScoreBadge
                                                        guesses={topCommentPreview.guesses}
                                                        expanded={false}
                                                        onToggle={() => {}}
                                                        compact={true}
                                                        isSolved={true}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-md tw-bg-violet-600/8 tw-border tw-border-violet-600/20 tw-mt-1">
                                                    <div className="tw-flex tw-flex-col tw-gap-0.5 tw-blur-sm">
                                                        {Array.from({ length: 2 }, (_, i) => (
                                                            <div key={i} className="tw-flex tw-gap-0.5">
                                                                {Array.from({ length: 5 }, (_, j) => (
                                                                    <div
                                                                        key={j}
                                                                        className="tw-w-1 tw-h-1 tw-rounded-sm tw-bg-violet-600"
                                                                    />
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <span className="tw-text-xs tw-text-gray-500">
                                                        {!isUserAuthenticated
                                                            ? "Sign in to view scores"
                                                            : "Complete puzzle to view scores"}
                                                    </span>
                                                </div>
                                            )
                                        ) : (
                                            <div className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-md tw-bg-violet-600/8 tw-border tw-border-violet-600/20 tw-mt-1">
                                                <div className="tw-flex tw-flex-col tw-gap-0.5 tw-blur-sm">
                                                    {Array.from({ length: 2 }, (_, i) => (
                                                        <div key={i} className="tw-flex tw-gap-0.5">
                                                            {Array.from({ length: 5 }, (_, j) => (
                                                                <div
                                                                    key={j}
                                                                    className="tw-w-1 tw-h-1 tw-rounded-sm tw-bg-violet-600"
                                                                />
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                                <span className="tw-text-xs tw-text-gray-500">Score hidden by user</span>
                                            </div>
                                        )}
                                    </>
                                )}
                                {topCommentPreview.body && (
                                    <p
                                        className="tw-m-0 tw-mt-1 tw-text-sm tw-leading-[1.45] tw-text-[#374151] tw-font-normal tw-line-clamp-3"
                                        style={{ fontFamily: "Inter, sans-serif" }}
                                        title={topCommentPreview.body}
                                    >
                                        {topCommentPreview.body}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div
            ref={propertyRef as React.RefObject<HTMLDivElement>}
            className="tw-mb-4 tw-border tw-border-gray-200/90 tw-bg-white tw-shadow-[0_4px_16px_-4px_rgba(15,23,42,0.1),0_2px_6px_-2px_rgba(15,23,42,0.06)]"
        >
            <BaseFeedCard
                leadingIcon={leadingIcon}
                title={headerTitle}
                meta={timestampLabel}
                content={
                    hasBody ? (
                        <div className="tw-mt-4">
                            <ShowMore
                                lines={2}
                                more="see more"
                                less="see less"
                                anchorClass="tw-border-0 tw-bg-transparent tw-p-0 tw-m-1 tw-text-[14px] tw-font-semibold tw-text-[#1F2937] hover:tw-underline"
                                className="tw-text-[#374151] tw-text-[14px] tw-leading-[1.45] tw-font-normal tw-mb-0"
                                onClick={(event) => event.stopPropagation()}
                            >
                                {bodyText}
                            </ShowMore>
                        </div>
                    ) : null
                }
                headerRight={
                    isPuzzleSharerFollowEligible ? (
                        <button
                            type="button"
                            onClick={handleFollowClick}
                            disabled={isFollowPending}
                            className={clsx(
                                "tw-rounded-full tw-px-4 tw-py-1.5 tw-text-sm tw-font-semibold tw-transition-colors",
                                isFollowing
                                    ? "tw-bg-gray-200 tw-text-gray-700 hover:tw-bg-gray-300"
                                    : "tw-bg-[#F69929] tw-text-white hover:tw-bg-[#e78a1c]",
                            )}
                        >
                            {isFollowing ? "Following" : "Follow"}
                        </button>
                    ) : null
                }
                media={media}
                badgeOverlay={badgeOverlay}
                actions={actions}
                onClick={onNavigate}
            />
            <GuestAuthPromptModal
                show={showGuestAuthPrompt}
                onClose={() => setShowGuestAuthPrompt(false)}
                onGoogle={handleGoogle}
                onEmail={handleEmail}
                description={guestAuthDescription} // description
                source={guestAuthSource} // source
            />
        </div>
    );
};

export default PuzzleFeedCard;
